import React from 'react';
import { useQuery } from '@apollo/client';
import { Company, ShopCart, User } from '../models';
import { GET_USER, GET_USER_IN_SHOP_CART_CONTEXT } from '../graphql/queries';
import { UserContext } from './UserContext';

type TShopCartContext = {
  shopCart: ShopCart;
  setShopCart: React.Dispatch<React.SetStateAction<ShopCart>>;
  refreshShopCart: () => Promise<void>;
};

export const ShopCartContext = React.createContext<TShopCartContext>({
  shopCart: null,
  setShopCart: null,
  refreshShopCart: null,
});

interface ShopCartContextProviderProps {
  children: React.ReactNode;
}

export function ShopCartContextProvider({
  children,
}: ShopCartContextProviderProps) {
  const [shopCart, setShopCart] = React.useState<ShopCart>(null);
  const { user } = React.useContext(UserContext);

  const { data, loading, error, fetchMore } = useQuery<{ user: User }>(
    GET_USER_IN_SHOP_CART_CONTEXT,
    {
      variables: {
        filter: {
          _id: user?._id,
        },
      },
      fetchPolicy: 'network-only',
    }
  );

  const refreshShopCart = async () => {
    await fetchMore({
      variables: {
        filter: {
          _id: user?._id,
        },
      },
    });
  };

  React.useEffect(
    function syncShopCart() {
      if (data && !loading) {
        const _shopCart = (data?.user?.company as Company)?.shopCart ?? null;
        setShopCart(_shopCart || (user?.company as Company)?.shopCart);
      }
      if (error) {
        setShopCart((user?.company as Company)?.shopCart);
      }
    },
    [data, loading, user, error]
  );
  const value = React.useMemo(
    () => ({ shopCart, setShopCart, refreshShopCart }),
    [shopCart]
  );
  return (
    <ShopCartContext.Provider value={value}>
      {loading ? null : children}
    </ShopCartContext.Provider>
  );
}
